import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/use-locale';
import { cn } from '@/lib/utils';
import { ModeToggle } from '@/components/mode-toggle';
import { LocaleSwitcher } from '@/components/locale-switcher';
import { Button } from '@/components/ui/button';
import { Menu, X, Search } from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { key: 'services', to: 'servicios' },
  { key: 'portfolio', to: 'portfolio' },
  { key: 'process', to: 'proceso' },
  { key: 'about', to: 'sobre-el-servicio' },
  { key: 'faq', to: 'faq' },
  { key: 'contact', to: 'contacto' },
] as const;

interface HeaderProps {
  onCommandPalette?: () => void;
  mostrarAccesoPrivado?: boolean;
}

export function Header({ onCommandPalette, mostrarAccesoPrivado = true }: HeaderProps = {}) {
  const { t } = useTranslation('common');
  const { lang } = useLocale();
  const [mobileOpen, setMobileOpen] = useState(false);
  const base = `/${lang}`;

  return (
    <header className="sticky top-0 z-40 border-b border-border/80 bg-background/90 shadow-[0_1px_0_hsl(var(--border)/0.35)] backdrop-blur-xl supports-[backdrop-filter]:bg-background/75">
      <div className="container-editorial flex h-16 items-center justify-between">
        <Link
          to={`${base}/`}
          className="flex items-center gap-2.5"
          aria-label={t('accessibility.home', { name: t('app.name') })}
        >
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-primary-foreground font-display font-bold text-sm shadow-sm ring-1 ring-primary/20">
            NT
          </span>
          <span className="font-display font-semibold text-base hidden sm:inline">
            {t('app.name')}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm" aria-label={t('accessibility.mainNav')}>
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={`${base}/${item.to}`}
              className={({ isActive }) =>
                cn(
                  'transition-colors',
                  isActive ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                )
              }
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LocaleSwitcher className="hidden sm:inline-flex" />
          <ModeToggle />
          {mostrarAccesoPrivado ? (
            <Link
              to={`${base}/login`}
              className="hidden sm:inline-flex text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
            >
              {t('nav.login')}
            </Link>
          ) : null}
          <Button asChild size="sm" className="hidden md:inline-flex">
            <Link to={`${base}/contacto`}>{t('nav.signup')}</Link>
          </Button>
          {onCommandPalette ? (
            <button
              type="button"
              onClick={onCommandPalette}
              className="icon-btn hidden sm:inline-flex"
              aria-label={t('accessibility.openCommands')}
              title="⌘K"
            >
              <Search className="w-4 h-4" />
            </button>
          ) : null}
          <button
            type="button"
            className="icon-btn md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={t('accessibility.openMenu')}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="md:hidden border-t border-border bg-background" aria-label={t('accessibility.mobileNav')}>
          <div className="container-editorial py-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={`${base}/${item.to}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'block px-3 py-2 rounded-md text-sm',
                    isActive ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:bg-muted'
                  )
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            {mostrarAccesoPrivado ? (
              <NavLink
                to={`${base}/login`}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted"
              >
                {t('nav.login')}
              </NavLink>
            ) : null}
            <NavLink
              to={`${base}/contacto`}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-primary"
            >
              {t('nav.signup')}
            </NavLink>
            <div className="pt-2 border-t border-border mt-2">
              <LocaleSwitcher />
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
